/**
 * PUBLIC consent self-service — `/crm/public/consent*`.
 *
 * These are the ONLY calls in the shell that are meant to work with no session
 * at all. The caller is a DATA PRINCIPAL: an external contact who received a
 * DPDP consent notice by email and clicked "Manage or withdraw consent". They
 * have no Clarix account, so the page must render signed-out — hence a public
 * apex route, not `/crm/*` (which needs a session, a workspace, and an app
 * grant).
 *
 * Authorisation is the signed token in the emailed link, passed through
 * verbatim. The shell never parses or trusts it — crm-api verifies the
 * signature, the audience, and the workspace the token names.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@clarix/api-client';
import { unwrap, type ApiResponse } from './envelope';

/** One purpose the notice covered. Each is withdrawn/re-granted on its own. */
export interface PublicConsentPurpose {
  purpose_code: string;
  purpose_label: string;
  purpose_description: string;
  is_active: boolean;
  granted_at: string | null;
  revoked_at: string | null;
}

export interface PublicConsentView {
  entity_type: string;
  /**
   * Every purpose of the capture event the notice described. One email now
   * covers them all, so this is a list, not a single value.
   */
  purposes: PublicConsentPurpose[];
  /**
   * The period actually ENFORCED for this record type (the admin's Retention
   * Policy), not the per-purpose notice default — see `retention_source`. It is
   * a property of the record, so it is one number for the whole page.
   */
  retention_days: number;
  retention_source: 'retention_policy' | 'consent_template';
  controller_name: string;
  support_email: string;
  subject_name: string;
}

const consentKey = (token: string) => ['public-consent', token] as const;

/** GET /crm/public/consent?token=… — the state the page renders. */
export function usePublicConsent(token: string | null) {
  return useQuery({
    queryKey: token ? consentKey(token) : (['public-consent', '__nil__'] as const),
    queryFn: async (): Promise<PublicConsentView> => {
      const res = await getApiClient().get<ApiResponse<PublicConsentView>>('/crm/public/consent', {
        params: { token },
      });
      return unwrap(res.data);
    },
    enabled: !!token,
    // An invalid or expired link is a terminal answer, not a blip — retrying
    // just delays the "contact the DPO" message the recipient needs to see.
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * POST /crm/public/consent/{revoke,grant} for ONE purpose.
 *
 * POST rather than a link the recipient follows: corporate mail scanners
 * prefetch every href in a message, and a GET that mutated would withdraw
 * consent the moment the email was delivered.
 *
 * The response is the whole page's new state, so it goes straight into the
 * cache — there is nothing to refetch.
 */
function useConsentMutation(action: 'revoke' | 'grant', token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (purpose: string): Promise<PublicConsentView> => {
      const res = await getApiClient().post<ApiResponse<PublicConsentView>>(
        `/crm/public/consent/${action}`,
        { token, purpose },
      );
      return unwrap(res.data);
    },
    onSuccess: (data) => {
      if (token) qc.setQueryData(consentKey(token), data);
    },
  });
}

export const useRevokeConsent = (token: string | null) => useConsentMutation('revoke', token);
export const useGrantConsent = (token: string | null) => useConsentMutation('grant', token);
